import { customPropertyMapSchema } from '@detect/shared';

/**
 * Editing support for a resource's `custom.*` properties (#37). The API
 * accepts ONLY `custom.*` from clients (`customPropertyMapSchema`:
 * system.* / auto.* are server- and discovery-managed, and accepting them
 * would let a tenant spoof classification and inventory provenance), and a
 * PATCH replaces the custom.* map wholesale. This module is the pure,
 * tested core of the editor and takes its verdicts from that same shared
 * schema, so the form cannot drift from what the API will accept.
 */

export const CUSTOM_PREFIX = 'custom.';

/** One editable row. `name` is the part AFTER `custom.` (the prefix is
 *  fixed in the UI, so another namespace cannot be typed by accident). */
export interface PropertyRow {
  id: number;
  name: string;
  value: string;
}

export interface RowErrors {
  name?: string;
  value?: string;
}

export type PropertyValidation =
  | { ok: true; properties: Record<string, string> }
  | { ok: false; errors: Record<number, RowErrors> };

let rowSeq = 0;
export function newRow(name = '', value = ''): PropertyRow {
  rowSeq += 1;
  return { id: rowSeq, name, value };
}

/** The custom.* entries of a property map as editable rows, by key. */
export function rowsFromProperties(properties: Record<string, string>): PropertyRow[] {
  return Object.entries(properties)
    .filter(([key]) => key.startsWith(CUSTOM_PREFIX))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, value]) => newRow(key.slice(CUSTOM_PREFIX.length), value));
}

/** The server/discovery-managed entries — shown read-only. */
export function managedProperties(properties: Record<string, string>): [string, string][] {
  return Object.entries(properties)
    .filter(([key]) => !key.startsWith(CUSTOM_PREFIX))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

const KEY_HINT =
  'Use lowercase letters, digits, "_", "." or "-" (starting with a letter, digit or "_").';

/** A typed name → the full key. Tolerates a pasted `custom.` prefix. */
function fullKey(rawName: string): string {
  const trimmed = rawName.trim();
  return trimmed.startsWith(CUSTOM_PREFIX) ? trimmed : `${CUSTOM_PREFIX}${trimmed}`;
}

const isBlank = (row: PropertyRow) => row.name.trim() === '' && row.value === '';

/**
 * Validate the rows exactly as the API will: namespace rule, key shape and
 * value length come from `customPropertyMapSchema`; duplicates are caught
 * here because a JS object would silently keep only the last. Entirely
 * blank rows (an "Add property" left empty) are ignored, not errors.
 */
export function validateRows(rows: PropertyRow[]): PropertyValidation {
  const errors: Record<number, RowErrors> = {};
  const properties: Record<string, string> = {};
  const seen = new Map<string, number>();

  for (const row of rows) {
    if (isBlank(row)) continue;
    const rowErrors: RowErrors = {};
    const name = row.name.trim();

    if (name === '') {
      rowErrors.name = 'Name is required.';
    } else if (/^(system|auto)\./.test(name)) {
      rowErrors.name =
        'system.* and auto.* properties are managed by Detect; only custom.* can be set here.';
    } else {
      const key = fullKey(name);
      if (!customPropertyMapSchema.keySchema.safeParse(key).success) {
        rowErrors.name = KEY_HINT;
      } else if (seen.has(key)) {
        rowErrors.name = 'Duplicate name.';
      } else {
        seen.set(key, row.id);
        if (!customPropertyMapSchema.valueSchema.safeParse(row.value).success) {
          rowErrors.value = 'Value is too long (max 4096 characters).';
        } else {
          properties[key] = row.value;
        }
      }
    }
    if (rowErrors.name !== undefined || rowErrors.value !== undefined) {
      errors[row.id] = rowErrors;
    }
  }

  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, properties };
}

export function sameProperties(
  a: Record<string, string>,
  b: Record<string, string>
): boolean {
  const ak = Object.keys(a);
  return ak.length === Object.keys(b).length && ak.every((k) => b[k] === a[k]);
}

/** The custom.* subset of a property map. */
export function customOnly(properties: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => key.startsWith(CUSTOM_PREFIX))
  );
}

/**
 * What to put in a PATCH: nothing when the custom.* map is unchanged (so an
 * unrelated edit never rewrites properties — the server still reconciles
 * targeting on every resource update either way); the complete new custom.*
 * map otherwise (the API replaces it wholesale, so removing a row removes
 * the property). Never includes system.* / auto.*.
 */
export function propertiesPatch(
  original: Record<string, string>,
  rows: PropertyRow[]
):
  | { ok: true; patch: { properties?: Record<string, string> } }
  | { ok: false; errors: Record<number, RowErrors> } {
  const v = validateRows(rows);
  if (!v.ok) return v;
  return {
    ok: true,
    patch: sameProperties(customOnly(original), v.properties)
      ? {}
      : { properties: v.properties },
  };
}
